import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Download, 
  Database,
  AlertTriangle,
  BarChart3,
  FileText
} from 'lucide-react';
import CourseMigrationService from '@/services/CourseMigrationService';
import { useToast } from '@/hooks/use-toast';

interface MigrationStats {
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

interface CourseStats {
  totalCourses: number;
  coursesByCategory: Record<string, number>;
  totalModules: number;
}

const CourseMigrationPanel: React.FC = () => {
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationStats, setMigrationStats] = useState<MigrationStats | null>(null);
  const [courseStats, setCourseStats] = useState<CourseStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const { toast } = useToast();

  const handleStartMigration = async () => {
    try {
      setMigrationRunning(true);
      setMigrationStats(null);
      
      toast({
        title: "Migration Started",
        description: "Beginning automated migration of 114+ legacy courses to database...",
      });

      // Call the migration service which now uses the edge function
      const results = await CourseMigrationService.migrateAllCourses();
      setMigrationStats(results);

      if (results.successful > 0) {
        toast({
          title: "Migration Completed Successfully! 🎉",
          description: `Successfully migrated ${results.successful} courses from legacy system. ${results.failed} failed.`,
        });
        
        // Refresh stats after migration
        await loadCourseStats();
      } else {
        toast({
          title: "Migration Failed",
          description: "No courses were migrated successfully. Check errors below.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast({
        title: "Migration Error",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setMigrationRunning(false);
    }
  };

  const loadCourseStats = async () => {
    try {
      setLoadingStats(true);
      const stats = await CourseMigrationService.getCourseStats();
      setCourseStats(stats);
    } catch (error) {
      console.error('Error loading course stats:', error);
      toast({
        title: "Error",
        description: "Failed to load course statistics",
        variant: "destructive"
      });
    } finally {
      setLoadingStats(false);
    }
  };

  React.useEffect(() => {
    loadCourseStats();
  }, []);

  const getMigrationProgress = () => {
    if (!migrationStats) return 0;
    return Math.round(((migrationStats.successful + migrationStats.failed) / migrationStats.total) * 100);
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Course Migration Center</h2>
          <p className="text-muted-foreground">
            Migrate legacy courses (114+) to the database system
          </p>
        </div>
        <Button
          onClick={handleStartMigration}
          disabled={migrationRunning}
          size="lg"
          className="gap-2"
        >
          {migrationRunning ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Migrating...
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Start Migration
            </>
          )}
        </Button>
      </div>

      {/* Current Database Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Current Database Statistics
          </CardTitle>
          <CardDescription>
            Overview of courses currently in the database
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStats ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading statistics...</span>
            </div>
          ) : courseStats ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-2xl font-bold text-primary">{courseStats.totalCourses}</div>
                <div className="text-sm text-muted-foreground">Total Courses</div>
              </div>
              <div className="text-center p-4 bg-secondary/10 rounded-lg">
                <div className="text-2xl font-bold text-secondary-foreground">{courseStats.totalModules}</div>
                <div className="text-sm text-muted-foreground">Total Modules</div>
              </div>
              <div className="text-center p-4 bg-accent/10 rounded-lg">
                <div className="text-2xl font-bold text-accent-foreground">
                  {Object.keys(courseStats.coursesByCategory).length}
                </div>
                <div className="text-sm text-muted-foreground">Active Categories</div>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Unable to load course statistics. Please try refreshing.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Migration Progress */}
      {migrationRunning && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Migration in Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Progress value={getMigrationProgress()} className="w-full" />
              <div className="text-center text-sm text-muted-foreground">
                {migrationStats ? (
                  `${migrationStats.successful + migrationStats.failed} of ${migrationStats.total} courses processed`
                ) : (
                  'Initializing migration...'
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Migration Results */}
      {migrationStats && !migrationRunning && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {migrationStats.failed === 0 ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              Migration Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{migrationStats.total}</div>
                <div className="text-sm text-muted-foreground">Total Courses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{migrationStats.successful}</div>
                <div className="text-sm text-muted-foreground">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{migrationStats.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
            </div>

            {migrationStats.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">Migration Errors:</div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {migrationStats.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-xs">{error}</div>
                    ))}
                    {migrationStats.errors.length > 10 && (
                      <div className="text-xs font-semibold">
                        ... and {migrationStats.errors.length - 10} more errors
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Distribution */}
      {courseStats && courseStats.totalCourses > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Courses by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(courseStats.coursesByCategory)
                .sort(([,a], [,b]) => b - a)
                .map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm font-medium capitalize">
                      {category.replace('-', ' ')}
                    </span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Legacy System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>✅ System Ready:</strong> Database categories migrated (35), edge function deployed, 
                ready to transfer all 114+ legacy courses automatically.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Legacy System:</strong>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  <li>• 114+ courses across 35 categories</li>
                  <li>• File-based course storage</li>
                  <li>• Manual course management</li>
                </ul>
              </div>
              <div>
                <strong>Database System:</strong>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  <li>• Centralized course management</li>
                  <li>• Real-time course updates</li>
                  <li>• Enhanced search and filtering</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseMigrationPanel;